import React, { useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { X, Film } from "lucide-react";
import { useAuth, useMessage } from "../../global-components/src";
import { REFLEXO_API_URL } from "../../global-components/src/utils/vars";
import { errorMessage } from "../../global-components/src/utils/text";
import MediaEditor from "../../components/media-editor";
import { useMediaEditor } from "../../hooks/useMediaEditor";
import { Reel } from "../../types";
import { FLOW_BUCKET } from "./flow-config";
import "./flow.css";
import { formatUploadBytes, type ResumableVideoUploadProgress, uploadResumableVideo } from "../../utils/resumable-video-upload";

interface UploadReelModalProps {
    onSuccess?: (reel: Reel) => void;
    onClose: () => void;
}

type Step = "pick" | "edit" | "uploading" | "done";

export default function UploadReelModal({ onSuccess, onClose }: UploadReelModalProps) {
    const { t } = useTranslation();
    const [step, setStep] = useState<Step>("pick");
    const [preview, setPreview] = useState<string | null>(null);
    const [editedFile, setEditedFile] = useState<File | null>(null);
    const [caption, setCaption] = useState("");
    const [audioTitle, setAudioTitle] = useState("");
    const [progress, setProgress] = useState(0);
    const [uploadProgress, setUploadProgress] = useState<ResumableVideoUploadProgress | null>(null);
    const uploadAbortRef = useRef<AbortController | null>(null);
    const [error, setError] = useState<string | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const editor = useMediaEditor();
    const headers = () => ({ Authorization: `Bearer ${token}` });

    const handleEditedFile = (file: File) => {
        setEditedFile(file);
        setPreview(URL.createObjectURL(file));
        setStep("edit");
    };

    const pickFile = (file: File) => {
        editor.open(file, {
            onConfirm: async (edited) => handleEditedFile(edited),
            onCancel: () => {},
        });
    };

    const onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const f = e.dataTransfer.files[0];
        if (f?.type.startsWith("video/")) pickFile(f);
    };

    const upload = async () => {
        if (!editedFile) return;
        setStep("uploading");
        setError(null);

        try {
            const controller = new AbortController();
            uploadAbortRef.current = controller;
            const uploaded = await uploadResumableVideo({
                api: axios,
                apiBaseUrl: REFLEXO_API_URL,
                headers: headers(),
                file: editedFile,
                signal: controller.signal,
                onProgress: next => {
                    setUploadProgress(next);
                    setProgress(Math.round((next.confirmedBytes / Math.max(next.totalBytes, 1)) * 100));
                },
            });

            const { data } = await axios.post(
                `${REFLEXO_API_URL}/reels`,
                { video_path: uploaded.path, caption, audio_title: audioTitle, bucket: FLOW_BUCKET },
                { headers: headers() }
            );

            setStep("done");
            setMessage(t("flow.upload.success"), "success");
            onSuccess?.(data.reel ?? data);
        } catch (err: any) {
            setError(errorMessage(err) ?? t("generics.error"));
            setStep("edit");
        } finally {
            uploadAbortRef.current = null;
            setUploadProgress(null);
        }
    };

    return (
        <div className="modal-upload-reel" onClick={(e) => e.target === e.currentTarget && onClose()}>
            <div className="card-upload-reel" onClick={(e) => e.stopPropagation()}>
                <div className="upload-reel-header">
                    <span className="upload-reel-title">{t("flow.upload.title")}</span>
                    <button className="upload-reel-close" onClick={onClose} aria-label={t("flow.actions.close")}>
                        <X size={18} />
                    </button>
                </div>

                <div className="upload-reel-body">
                    {step === "pick" && (
                        <div
                            className="upload-reel-dropzone"
                            onDrop={onDrop}
                            onDragOver={(e) => e.preventDefault()}
                            onClick={() => inputRef.current?.click()}
                        >
                            <Film size={44} />
                            <p>{t("flow.upload.dropHint")}</p>
                            <p>{t("flow.upload.dropSub")}</p>
                            <input
                                ref={inputRef}
                                type="file"
                                accept="video/*"
                                style={{ display: "none" }}
                                onChange={(e) => {
                                    const f = e.target.files?.[0];
                                    if (f) pickFile(f);
                                }}
                            />
                        </div>
                    )}

                    {(step === "edit" || step === "uploading") && preview && (
                        <div style={{ display: "flex", gap: 0, flex: 1, flexWrap: "wrap" }}>
                            <video src={preview} className="preview-video" controls muted loop playsInline />

                            <div className="upload-reel-form" style={{ flex: 1, minWidth: 240 }}>
                                <label>{t("flow.upload.captionLabel")}</label>
                                <textarea
                                    className="upload-reel-textarea"
                                    maxLength={500}
                                    placeholder={t("flow.upload.captionPlaceholder")}
                                    value={caption}
                                    onChange={(e) => setCaption(e.target.value)}
                                    disabled={step === "uploading"}
                                    rows={4}
                                />
                                <span style={{ fontSize: 12, color: "var(--reel-text-muted)", textAlign: "right" }}>
                                    {caption.length}/500
                                </span>

                                <label>{t("flow.upload.audioLabel")}</label>
                                <input
                                    className="upload-reel-input"
                                    placeholder={t("flow.upload.audioPlaceholder")}
                                    value={audioTitle}
                                    onChange={(e) => setAudioTitle(e.target.value)}
                                    disabled={step === "uploading"}
                                    maxLength={100}
                                />

                                {error && <p style={{ color: "#ff4d4d", fontSize: 13, margin: 0 }}>{error}</p>}

                                {step === "uploading" ? (
                                    <div className="upload-reel-progress-row" role="status">
                                        <div className="upload-reel-progress-bar">
                                            <div className="upload-reel-progress-fill" style={{ width: `${progress}%` }} />
                                        </div>
                                        <span>{progress}% {uploadProgress ? `· ${formatUploadBytes(uploadProgress.confirmedBytes)} / ${formatUploadBytes(uploadProgress.totalBytes)}` : ''}</span>
                                        {uploadProgress?.speedBytesPerSecond ? <small>{formatUploadBytes(uploadProgress.speedBytesPerSecond)}/s</small> : null}
                                        <button type="button" className="upload-reel-btn-secondary" onClick={() => uploadAbortRef.current?.abort()}>Cancelar</button>
                                    </div>
                                ) : (
                                    <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
                                        <button
                                            className="upload-reel-btn-secondary"
                                            onClick={() => {
                                                setStep("pick");
                                                setPreview(null);
                                                setEditedFile(null);
                                            }}
                                        >
                                            {t("flow.upload.changeVideo")}
                                        </button>
                                        <button className="upload-reel-btn-primary" onClick={upload}>
                                            {t("flow.upload.publish")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step === "done" && (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: 60 }}>
                            <Film size={56} />
                            <p style={{ fontWeight: 700, fontSize: 20, margin: 0 }}>{t("flow.upload.done")}</p>
                            <button className="upload-reel-btn-primary" onClick={onClose}>
                                {t("flow.actions.close")}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {editor.isOpen && <MediaEditor {...editor.props} confirmLabel={t("flow.upload.useVideo")} />}
        </div>
    );
}
